import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  Download, Loader2, Pin, DownloadCloud, Copy, Check,
  Clock, Lightbulb, Hash, Globe, ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';
import { PinterestPinGenerator } from '../utils/pinterestPinGenerator';
import PinterestScheduler from './PinterestScheduler';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia',
  'Germany', 'France', 'Norway', 'Sweden', 'Denmark',
  'Netherlands', 'Spain', 'Italy', 'Brazil', 'India',
  'Japan', 'South Korea', 'Mexico', 'South Africa'
];

/* ─── Copyable Field ─── */
const CopyField = ({ label, value, icon: Icon, multiline }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    const text = Array.isArray(value) ? value.join(' ') : value;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const displayValue = Array.isArray(value) ? value.join(' ') : value;

  return (
    <div className="group relative">
      <div className="flex items-start gap-2 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-stone-400 mt-0.5 flex-shrink-0" />}
        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 font-sans">{label}</span>
      </div>
      <div className="flex items-start gap-2">
        <p className={`text-sm font-sans text-stone-700 leading-relaxed flex-1 ${multiline ? '' : 'truncate'}`}>
          {displayValue}
        </p>
        <button
          onClick={handleCopy}
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-stone-100"
          data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-stone-400" />}
        </button>
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const PinterestPinView = ({ content, productType }) => {
  const [pins, setPins] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selectedPin, setSelectedPin] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [country, setCountry] = useState('United States');
  const [showCountryPicker, setShowCountryPicker] = useState(false);

  // Marketing kit state
  const [marketingKit, setMarketingKit] = useState(null);
  const [loadingKit, setLoadingKit] = useState(false);
  const [kitError, setKitError] = useState(null);
  const [expandedPin, setExpandedPin] = useState(0);

  const deriveTypeKey = (title) => {
    if (!title) return 'default';
    const lower = title.toLowerCase();
    if (lower.includes('budget') || lower.includes('finance') || lower.includes('money')) return 'budget-planner';
    if (lower.includes('meal') || lower.includes('food') || lower.includes('recipe')) return 'meal-planner';
    if (lower.includes('workout') || lower.includes('fitness') || lower.includes('exercise')) return 'workout-planner';
    if (lower.includes('goal') || lower.includes('habit') || lower.includes('journal')) return 'goal-workbook';
    return 'default';
  };

  const generatePins = useCallback(() => {
    if (!content) return;
    setGenerating(true);
    setTimeout(() => {
      try {
        const generator = new PinterestPinGenerator('peachy-calm');
        const productData = {
          title: content.title || 'Digital Product',
          benefit1: content.slides?.[0]?.headline || 'Professional design',
          benefit2: content.slides?.[1]?.headline || 'Print-ready PDF',
          benefit3: content.slides?.[2]?.headline || 'Editable content',
        };
        const typeKey = productType || deriveTypeKey(content.title);
        const canvases = generator.generatePinsForProduct(productData, typeKey);
        const pinDataUrls = canvases.map((canvas, i) => ({
          id: i,
          dataUrl: canvas.toDataURL('image/png', 0.95),
          width: canvas.width,
          height: canvas.height,
        }));
        setPins(pinDataUrls);
        setSelectedPin(0);
      } catch (err) {
        console.error('Pin generation error:', err);
      }
      setGenerating(false);
    }, 100);
  }, [content, productType]);

  // Auto-generate on mount
  useEffect(() => {
    if (content && pins.length === 0) {
      generatePins();
    }
  }, [content, pins.length, generatePins]);

  // Fetch marketing kit
  const fetchMarketingKit = useCallback(async () => {
    if (!content || pins.length === 0) return;
    setLoadingKit(true);
    setKitError(null);

    try {
      const typeKey = productType || deriveTypeKey(content.title);

      // Get the hook texts from the generator
      const generator = new PinterestPinGenerator('peachy-calm');
      const hooks = generator.getHooks(typeKey);
      const hookTexts = hooks.map(h => h.text);

      // Build slides summary
      const slidesSummary = (content.slides || [])
        .map(s => s.headline)
        .join('. ');

      const response = await axios.post(
        `${API_URL}/api/content/pinterest-marketing`,
        {
          product_title: content.title || 'Digital Product',
          product_type: typeKey,
          pin_hooks: hookTexts,
          slides_summary: slidesSummary,
          country: country,
        }
      );

      setMarketingKit(response.data);
      setExpandedPin(0);
    } catch (err) {
      console.error('Marketing kit error:', err);
      setKitError('Failed to generate marketing kit. Try again.');
    }
    setLoadingKit(false);
  }, [content, pins.length, country, productType]);

  // Auto-fetch marketing kit once pins are generated
  useEffect(() => {
    if (pins.length > 0 && !marketingKit && !loadingKit) {
      fetchMarketingKit();
    }
  }, [pins.length, marketingKit, loadingKit, fetchMarketingKit]);

  const handleDownloadPin = (pin, index) => {
    const link = document.createElement('a');
    link.download = `${content.title || 'pin'}-pinterest-${index + 1}.png`;
    link.href = pin.dataUrl;
    link.click();
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    for (let i = 0; i < pins.length; i++) {
      handleDownloadPin(pins[i], i);
      await new Promise(r => setTimeout(r, 400));
    }
    setDownloading(false);
  };

  const handleCopyAll = (pinKit) => {
    if (!pinKit) return;
    const text = `TITLE: ${pinKit.title}\n\nDESCRIPTION: ${pinKit.description}\n\nALT TEXT: ${pinKit.alt_text}\n\nHASHTAGS: ${(pinKit.hashtags || []).join(' ')}\n\nBOARD: ${pinKit.board_suggestion}\n\nCAPTION: ${pinKit.caption}\n\nSAVE CTA: ${pinKit.save_cta}`;
    navigator.clipboard.writeText(text);
  };

  if (generating && pins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="pinterest-generating">
        <Loader2 className="w-10 h-10 animate-spin text-[#B49A88] mb-4" />
        <p className="font-sans text-stone-600 text-sm">Generating Pinterest pins...</p>
      </div>
    );
  }

  if (pins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="pinterest-empty">
        <Pin className="w-10 h-10 text-stone-300 mb-4" />
        <p className="font-sans text-stone-500 text-sm mb-4">No pins generated yet</p>
        <Button onClick={generatePins} className="bg-[#B49A88] text-white hover:bg-[#B49A88] rounded-full px-6 font-sans font-semibold">
          Generate Pins
        </Button>
      </div>
    );
  }

  const current = pins[selectedPin];
  const currentPinKit = marketingKit?.pins?.[selectedPin];

  return (
    <div data-testid="pinterest-pin-view" className="space-y-5">
      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#E60023] flex items-center justify-center">
            <Pin className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-sans font-bold text-stone-800 text-sm leading-none">Pinterest Pins</h3>
            <p className="font-sans text-stone-400 text-xs mt-0.5">{pins.length} pins ready</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Country picker */}
          <div className="relative" data-testid="country-picker">
            <button
              onClick={() => setShowCountryPicker(!showCountryPicker)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-full border border-stone-200 text-xs font-sans font-medium text-stone-600 hover:border-stone-300 transition-colors"
            >
              <Globe className="w-3.5 h-3.5" />
              {country}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showCountryPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto w-48">
                {COUNTRIES.map(c => (
                  <button
                    key={c}
                    data-testid={`country-${c.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => {
                      setCountry(c);
                      setShowCountryPicker(false);
                      setMarketingKit(null); // re-fetch with new country
                    }}
                    className={`w-full text-left px-3 py-2 text-xs font-sans hover:bg-stone-50 transition-colors ${
                      c === country ? 'text-[#B49A88] font-semibold bg-amber-50/50' : 'text-stone-600'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            data-testid="download-all-pins-btn"
            onClick={handleDownloadAll}
            disabled={downloading}
            variant="outline"
            className="rounded-full px-4 h-9 text-xs font-sans font-semibold border-stone-200"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <DownloadCloud className="w-3.5 h-3.5 mr-1.5" />}
            Download All
          </Button>
        </div>
      </div>

      {/* Main content: Pin + Marketing Kit side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Left: Pin preview */}
        <div className="space-y-4">
          <div
            className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-100"
            data-testid="pinterest-main-preview"
          >
            <img
              src={current.dataUrl}
              alt={`Pinterest pin ${selectedPin + 1}`}
              className="w-full h-auto block"
              draggable={false}
            />
            <button
              data-testid={`download-pin-${selectedPin}`}
              onClick={() => handleDownloadPin(current, selectedPin)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm shadow-md flex items-center justify-center hover:bg-white transition-colors"
            >
              <Download className="w-4 h-4 text-stone-700" />
            </button>
            <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white text-xs font-sans font-semibold px-2.5 py-1 rounded-full">
              Pin {selectedPin + 1} of {pins.length}
            </div>
          </div>

          {/* Thumbnails */}
          <div className="flex items-center justify-center gap-3">
            {pins.map((pin, i) => (
              <button
                key={i}
                data-testid={`pinterest-thumb-${i}`}
                onClick={() => { setSelectedPin(i); setExpandedPin(i); }}
                className={`relative flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${
                  selectedPin === i
                    ? 'border-[#E60023] scale-105 shadow-md'
                    : 'border-stone-200 opacity-60 hover:opacity-100 hover:border-stone-300'
                }`}
                style={{ width: '48px', height: '72px' }}
              >
                <img src={pin.dataUrl} alt={`Pin ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
              </button>
            ))}
          </div>

          <div className="text-center">
            <button
              data-testid="regenerate-pins-btn"
              onClick={() => { generatePins(); setMarketingKit(null); }}
              disabled={generating}
              className="text-xs font-sans font-medium text-stone-400 hover:text-[#B49A88] transition-colors"
            >
              {generating ? 'Generating...' : 'Regenerate pins'}
            </button>
          </div>
        </div>

        {/* Right: Marketing Kit */}
        <div className="space-y-4" data-testid="marketing-kit-panel">
          {/* Loading state */}
          {loadingKit && (
            <div className="bg-white rounded-2xl border border-stone-100 p-8 flex flex-col items-center justify-center" data-testid="marketing-kit-loading">
              <Loader2 className="w-8 h-8 animate-spin text-[#B49A88] mb-3" />
              <p className="font-sans text-stone-600 text-sm font-medium">Generating marketing kit...</p>
              <p className="font-sans text-stone-400 text-xs mt-1">Optimizing for {country}</p>
            </div>
          )}

          {/* Error state */}
          {kitError && !loadingKit && (
            <div className="bg-white rounded-2xl border border-red-100 p-6 text-center">
              <p className="font-sans text-red-500 text-sm mb-3">{kitError}</p>
              <Button onClick={fetchMarketingKit} variant="outline" className="rounded-full text-xs font-sans font-semibold">
                Try Again
              </Button>
            </div>
          )}

          {/* Marketing Kit Content */}
          {marketingKit && !loadingKit && (
            <div className="space-y-4">
              {/* Best Time to Post */}
              {marketingKit.best_time_to_post && (
                <div className="bg-gradient-to-r from-[#E60023]/5 to-[#C49A6C]/5 rounded-2xl border border-[#E60023]/10 p-4" data-testid="best-time-to-post">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#E60023]" />
                    <h4 className="font-sans font-bold text-stone-800 text-xs">Best Time to Post</h4>
                  </div>
                  <p className="font-sans text-stone-800 text-sm font-semibold">
                    {marketingKit.best_time_to_post.day} at {marketingKit.best_time_to_post.time}
                    <span className="text-stone-400 font-normal text-xs ml-1">({marketingKit.best_time_to_post.timezone})</span>
                  </p>
                  {marketingKit.best_time_to_post.reason && (
                    <p className="font-sans text-stone-500 text-xs mt-1">{marketingKit.best_time_to_post.reason}</p>
                  )}
                </div>
              )}

              {/* Pin-specific marketing */}
              {marketingKit.pins?.map((pinKit, i) => (
                <div
                  key={i}
                  data-testid={`pin-marketing-${i}`}
                  className={`bg-white rounded-2xl border transition-all ${
                    expandedPin === i ? 'border-[#E60023]/20 shadow-sm' : 'border-stone-100'
                  }`}
                >
                  <div
                    onClick={() => { setExpandedPin(expandedPin === i ? -1 : i); setSelectedPin(i); }}
                    className="w-full flex items-center justify-between p-4 cursor-pointer"
                    data-testid={`pin-marketing-toggle-${i}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#E60023] text-white text-[10px] font-bold flex items-center justify-center font-sans">
                        {i + 1}
                      </div>
                      <span className="font-sans font-semibold text-stone-800 text-sm">Pin {i + 1} Kit</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CopyAllButton pinKit={pinKit} onCopy={() => handleCopyAll(pinKit)} />
                      {expandedPin === i ? (
                        <ChevronUp className="w-4 h-4 text-stone-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-stone-400" />
                      )}
                    </div>
                  </div>

                  {expandedPin === i && (
                    <div className="px-4 pb-4 space-y-3 border-t border-stone-50 pt-3">
                      <CopyField label="Title" value={pinKit.title} icon={Sparkles} />
                      <CopyField label="Description" value={pinKit.description} icon={null} multiline />
                      <CopyField label="Alt Text" value={pinKit.alt_text} icon={null} />
                      <CopyField label="Hashtags" value={pinKit.hashtags} icon={Hash} />
                      <CopyField label="Board" value={pinKit.board_suggestion} icon={null} />
                      <CopyField label="Save CTA" value={pinKit.save_cta} icon={null} />
                      <CopyField label="Caption" value={pinKit.caption} icon={null} multiline />
                    </div>
                  )}
                </div>
              ))}

              {/* Tips */}
              {marketingKit.tips && marketingKit.tips.length > 0 && (
                <div className="bg-amber-50/50 rounded-2xl border border-amber-100/50 p-4" data-testid="pinterest-tips">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    <h4 className="font-sans font-bold text-stone-800 text-xs">Pinterest Tips</h4>
                  </div>
                  <ul className="space-y-1.5">
                    {marketingKit.tips.map((tip, i) => (
                      <li key={i} className="font-sans text-stone-600 text-xs leading-relaxed flex items-start gap-2">
                        <span className="text-amber-400 mt-0.5">&#x2022;</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Refresh button */}
              <div className="text-center">
                <button
                  data-testid="refresh-marketing-kit-btn"
                  onClick={fetchMarketingKit}
                  disabled={loadingKit}
                  className="text-xs font-sans font-medium text-stone-400 hover:text-[#B49A88] transition-colors"
                >
                  {loadingKit ? 'Regenerating...' : 'Refresh marketing kit'}
                </button>
              </div>

              {/* Pinterest Scheduler */}
              <PinterestScheduler
                pins={pins}
                pinKits={marketingKit?.pins}
                content={content}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Copy All Mini Button ─── */
const CopyAllButton = ({ onCopy }) => {
  const [copied, setCopied] = useState(false);
  const handleClick = (e) => {
    e.stopPropagation();
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-sans font-semibold text-stone-400 hover:text-[#B49A88] hover:bg-amber-50/50 transition-colors"
      data-testid="copy-all-pin-kit"
    >
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy All'}
    </button>
  );
};

export default PinterestPinView;
